import React, { FC, useEffect, useMemo, useContext } from 'react'
import { push } from 'connected-react-router'

import CustomButton from '../../UI/Buttons/CustomButton'
import { Can,AbilityContext } from '../../../CASL/Can'
import { AbilityBuilder, Ability } from '@casl/ability';


import { useDispatch, useSelector } from '../../../store'
import {
  changeCalendarViewAction,
  setHeaderView,
  toggleInviteModalAction,
  toggleIsAddingPostAction,
  showApprovalListAction
} from '../../../store/application/slice'

import { setInitialStateAction } from '../../../store/modal/approvalPost/slice';

import './CalendarHeader.scss'
import { format } from 'date-fns'
import { generatePath, matchPath } from 'react-router-dom'
import { CALENDAT_VIEW, HEADER_VIEW, APPROVAL_LIST_TYPE } from '../../../store/application/types'
import { setPostToApproveAction } from '../../../store/managePost/slice'
import { AUTH_ROUTE } from '../../../pages/App/routes'
import { SOCIAL_NETWORK } from '../../../models/post'
import { selectNotApprovedPostsWithoutRequireRevision } from '../../../selectors/PostSelector'
import WhiteAddIcon from '../../UI/Icons/AddWhite'
import LoadableImage from '../../UI/LoadableImage'
import Roles from '../../../models/roles'
import { SETTING_NAME } from '../../../store/project/types';



interface AppProps {
  calendarApi?: any
}

const MainHeader: FC<AppProps> = ({ calendarApi }) => {
  const {
    calendarView,
    projectId,
    location,
    postsToApproval,
    postToApprove,
    postSocials,
    latestSchedule,
    view,
    user,
    approvalListType,
    timeZone
  } = useSelector((state) => ({
    calendarView: state.app.calendarView,
    projectId: state.project.currentProject.id,
    postToApprove: state.managePost.post,
    latestSchedule: state.managePost.latestSchedule,
    postSocials: state.managePost.postSocials,
    location: state.router.location,
    view: state.app.headerView,
    user: state.auth.user,
    postsToApproval: selectNotApprovedPostsWithoutRequireRevision(state),
    approvalListType: state.app.approvalListType,
    timeZone: state.project.currentProject.timezone
  }))

  const ability = useContext(AbilityContext);
  const { can, rules } = new AbilityBuilder(Ability);

  const dispatch = useDispatch()

  const switchView = (view: CALENDAT_VIEW) => {
    calendarApi.changeView(view)
    dispatch(changeCalendarViewAction(view))
  }

  const setIsAddingPost = () => {
    dispatch(toggleIsAddingPostAction())
  }

  const moveTo = (to: string) => {
    dispatch(push(to))
  }

  const generatePathToSocial = ({ social, postId }: { social: SOCIAL_NETWORK; postId: number }) => {
    return (
      generatePath(AUTH_ROUTE.APPROVAL_ITEM, {
        projectId: projectId as number,
        social
      }) +
      '?postId=' +
      postId
    )
  }

  const setPreviousPostToApprove = async () => {
    const { id: currentPostId } = postToApprove
    const indexCurrentPost = postsToApproval.findIndex(({ id }) => id === currentPostId)
    const indexPrevElement =
      (indexCurrentPost - 1 + postsToApproval.length) % postsToApproval.length
    if (indexCurrentPost !== indexPrevElement) {
      const post = postsToApproval[indexPrevElement]
      dispatch(setPostToApproveAction(post))
      moveTo(generatePathToSocial({ social: post.post_socials[0].social_network, postId: post.id }))
    }
  }

  const setNextPostToApprove = async () => {
    const { id: currentPostId } = postToApprove
    const indexCurrentPost = postsToApproval.findIndex(({ id }) => id === currentPostId)
    const indexNextElement = (indexCurrentPost + 1) % postsToApproval.length
    if (indexCurrentPost !== indexNextElement) {
      const post = postsToApproval[indexNextElement]
      dispatch(setPostToApproveAction(post))
      moveTo(generatePathToSocial({ social: post.post_socials[0].social_network, postId: post.id }))
    }
  }

  const checkActivePath = (path: AUTH_ROUTE) => {
    return matchPath(location.pathname, {
      path
    })
  }

  const onButtonBackClick = () => {
    if (checkActivePath(AUTH_ROUTE.APPROVAL_LIST)) {
      moveTo(
        generatePath(AUTH_ROUTE.CALENDAR, {
          projectId: projectId
        })
      )
    }
    if (checkActivePath(AUTH_ROUTE.APPROVAL_ITEM) || checkActivePath(AUTH_ROUTE.UNAPPROVE_ITEM)) {
      moveTo(
        generatePath(AUTH_ROUTE.APPROVAL_LIST, {
          projectId: projectId
        })
      )
    }
  }

  useEffect(()=>{
    if(user?.agency_role === Roles.CONTENT_CREATOR && user?.projects && projectId){
       if(!!user.projects.find(v=>v.id === projectId)?.setting.find(v=>v.name === SETTING_NAME.CONTENT_MANAGER && v.value === 'true')){
        for (let rule of ability.rules) {
          can(rule.action, rule.subject)
        }
        can('moveToApprovalList', 'Post');
        ability.update(rules);
       }
    }
  },[user, projectId])

  const setApprovalList = (type:string)=>{
    dispatch(showApprovalListAction(type))
    dispatch(setInitialStateAction())
  }

  // Conditionals to show data into header
  const isEnableChangeWeek = view === HEADER_VIEW.CALENDAR

  const isHidePageName = view === HEADER_VIEW.APPROVAL_ITEM

  const isShowPendingToApprove =
    view === HEADER_VIEW.CALENDAR ||
    view === HEADER_VIEW.CONTENT ||
    view === HEADER_VIEW.ANALYTICS ||
    view === HEADER_VIEW.REPORTS

  const isShowBackToCalendar = (view === HEADER_VIEW.APPROVALS && user?.agency_role === Roles.AGENCY_ADMIN) || (view === HEADER_VIEW.APPROVALS && user?.agency_role === Roles.CONTENT_CREATOR && !!user?.projects?.find(v=>v.id === projectId)?.setting.find(v=>v.name === SETTING_NAME.CONTENT_MANAGER && v.value === 'true'))

  const isShowCreatePostButton =
    view === HEADER_VIEW.CALENDAR ||
    view === HEADER_VIEW.CONTENT ||
    view === HEADER_VIEW.ANALYTICS ||
    view === HEADER_VIEW.REPORTS

  const isShowApprovedPost = 
  view === HEADER_VIEW.APPROVALS  

  const firstMedia = useMemo(() => {
    const postSocial = postSocials.find((social) => !!social.attachments[0])
    return postSocial && postSocial.attachments[0].object_data
  }, [postSocials])

  return (
    <div className='main-page-header'>
      {(!isHidePageName && !isShowBackToCalendar) && (
        <div className='main-page-header__page-name'>{`${view.charAt(0).toUpperCase()}${view
          .replaceAll('-', ' ')
          .slice(1)}`}</div>
      )}
      {(isShowBackToCalendar || isHidePageName) && (
        <CustomButton button='move' contentClass='arrow-left' onClick={onButtonBackClick}>
          Back
        </CustomButton>
      )}      
      <div className='main-page-header__view-navigation'>
      {isEnableChangeWeek && (
        <div className='post-action_content-title_strong main-page-header__view-navigation__timezone'>Times shown are in {timeZone} time </div>
      )}
        {isEnableChangeWeek && (
          <div className='main-page-header__view-navigation__button-wrapper'>
            <button
              className={`${calendarView === CALENDAT_VIEW.MONTH_VIEW && 'active'}`}
              onClick={() => switchView(CALENDAT_VIEW.MONTH_VIEW)}
            >
              Month
            </button>
            <button
              className={`${calendarView === CALENDAT_VIEW.WEEK_VIEW && 'active'}`}
              onClick={() => switchView(CALENDAT_VIEW.WEEK_VIEW)}
            >
              Week
            </button>
          </div>
        )}
        {!!postToApprove.id && !!latestSchedule.id && checkActivePath(AUTH_ROUTE.APPROVAL_ITEM) && (
          <div className='main-page-header__socials-navigation'>
            <div
              className='main-page-header__socials-navigation__button-move'
              onClick={setPreviousPostToApprove}
            />
            <div className='main-page-header__socials-navigation__post-preview'>
              {!!firstMedia && (
                <div className='post-cover' key={firstMedia?.public_url}>
                  <LoadableImage
                    media={
                      firstMedia?.previews?.small ||
                      firstMedia?.previews?.original ||
                      firstMedia?.public_url
                    }
                  />
                </div>
              )}
              <div className='post-data'>
                <div className='post-data__post-type'>
                  {postToApprove.type.replace('_', ' ').charAt(0).toUpperCase() +
                    postToApprove.type.replace('_', ' ').slice(1)}
                </div>
                <div className='post-data__post-date'>
                  {format(
                    new Date(`${latestSchedule.date}T${latestSchedule.time}`),
                    'dd/MM/yy h:mm a'
                  )}
                </div>
              </div>
            </div>
            <div
              className='main-page-header__socials-navigation__button-move'
              onClick={setNextPostToApprove}
            />
          </div>
        )}
      </div>

      <div className='main-page-header__actions'>
        {isShowPendingToApprove && (
          <Can I='moveToApprovalList' a='Post'>
            <CustomButton
              className='main-page-header__actions__to-approve'
              onClick={() =>
                moveTo(
                  generatePath(AUTH_ROUTE.APPROVAL_LIST, {
                    projectId: projectId as number
                  })
                )
              }
              button='subtle'
              size='small'
            >
              {/* <div className='main-page-header__actions__to-approve__quantity'>2</div> */}
              Pending to approve
            </CustomButton>
          </Can>
        )}
          {isShowApprovedPost && (	
            <Can I='moveToUnapprovalList' a='Post'>	
              <CustomButton	
                  className='main-page-header__actions__create-content'	
                  button={ approvalListType === APPROVAL_LIST_TYPE.PENDING ? 'primary':'subtle'}
                  size='small'
                  onClick={() => setApprovalList(APPROVAL_LIST_TYPE.PENDING)}	
                >	
                  Pending to approve	
                </CustomButton>	
              
                  <CustomButton	
                    className='main-page-header__actions__create-content'	
                    button={ approvalListType === APPROVAL_LIST_TYPE.APPROVED ? 'primary':'subtle'}
                    size='small'	
                    onClick={() => setApprovalList(APPROVAL_LIST_TYPE.APPROVED)}
                  >	
                    Approved content	
                  </CustomButton>	
            </Can>	
          )}	
        {/* <CustomButton
          button='secondary'
          className='main-page-header__actions__notification-button not-empty'
          // onClick={() => moveTo('')}
          size='small'
        >
          <img src={NotificationIcon} alt='Notification' />
        </CustomButton> */}
        {view === HEADER_VIEW.TEAM || view === HEADER_VIEW.CLIENT ? (
          <CustomButton
            className='main-page-header__actions__create-content'
            onClick={() => dispatch(toggleInviteModalAction())}
            button='primary'
            size='small'
          >
            <WhiteAddIcon />
            Invite new member
          </CustomButton>
        ) : (
          isShowCreatePostButton && (
            <Can I='create' a='Post'>
              <CustomButton
                className='main-page-header__actions__create-content'
                onClick={() => setIsAddingPost()}
                button='primary'
                size='small'
              >
                <WhiteAddIcon />
                Create content
              </CustomButton>
            </Can>
          )
        )}
      </div>
    </div>
  )
}

export default MainHeader
